import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { PositionControlLine } from '../PositionControlLine/PositionControlLine';
import { PositionControlSquare } from '../PositionControlSquare/PositionControlSquare';
import type { PositionControlProps, PositionEnableProps } from '@/types/layout';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';

interface Props {
  text: string;
  className: string;
  position?: PositionControlProps;
  positionAnchors?: PositionEnableProps;
  onChange?: (side: keyof PositionControlProps, value: string) => void;
  onChangeComplete?: (
    side: keyof PositionControlProps,
    value: string,
    initialValue: string,
  ) => void;
  onClick?: (side: keyof PositionEnableProps) => void;
  onClickPositionControlSquare?: () => void;
}

const DEFAULT_POSITION_ANCHORS: PositionEnableProps = {
  enableTop: false,
  enableRight: false,
  enableBottom: false,
  enableLeft: false,
  enabled: false,
};

export const PositionControl: React.FC<Props> = ({
  className,
  position = { top: '', right: '', bottom: '', left: '' },
  positionAnchors = DEFAULT_POSITION_ANCHORS,
  onChange,
  onChangeComplete,
  onClick,
  onClickPositionControlSquare,
}) => {
  const [initialState, setInitialState] = useState(position);
  const [values, setValues] = useState(position);
  const valuesRef = useRef(values);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);
  const normalizedPosition = useMemo(
    () => ({
      top: position.top ?? '',
      right: position.right ?? '',
      bottom: position.bottom ?? '',
      left: position.left ?? '',
    }),
    [position.top, position.right, position.bottom, position.left],
  );

  useEffect(() => {
    setValues((prev) => {
      const isSame =
        normalizedPosition.top === prev.top &&
        normalizedPosition.right === prev.right &&
        normalizedPosition.bottom === prev.bottom &&
        normalizedPosition.left === prev.left;

      return isSame ? prev : normalizedPosition;
    });
  }, [normalizedPosition]);

  const handleInputChangeComplete =
    (side: keyof PositionControlProps) =>
    (e: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => {
      const initialValue = initialState[side] || '';
      onChangeComplete?.(side, e.currentTarget.value, initialValue);
      setInitialState({
        ...position,
        [side]: e.currentTarget.value,
      });
    };

  const handleInputChange = (side: keyof PositionControlProps, value: number) => {
    const nextValue = String(value);
    setValues((prev) => {
      if (prev[side] === nextValue) return prev;
      return { ...prev, [side]: nextValue };
    });
    if (valuesRef.current[side] !== nextValue) {
      onChange?.(side, nextValue);
    }
  };

  const handlePositionControlLineClick = (side: keyof PositionEnableProps) => {
    onClick?.(side);
  };

  const PositionControlSquareClick = () => {
    onClickPositionControlSquare?.();
  };

  return (
    <div
      className={clsx('flex w-full max-w-[216px] flex-col items-center justify-center', className)}
    >
      <div className="flex flex-col items-center gap-1">
        <NumberField
          className="h-8 min-h-8 w-12 min-w-10 text-center"
          max={5120}
          min={-5120}
          onChange={(val) => handleInputChange('top', val)}
          onChangeComplete={handleInputChangeComplete('top')}
          step={1}
          value={Number((values.top || '0px')?.replace('px', ''))}
          disabled={!positionAnchors.enableTop}
        />
        <PositionControlLine
          value={positionAnchors.enableTop}
          state={'vertical'}
          disabled={false}
          onClick={() => handlePositionControlLineClick('enableTop')}
        />
      </div>
      <div className="flex items-center gap-1">
        <div className="flex flex-row items-center gap-1">
          <NumberField
            className="h-8 min-h-8 w-12 min-w-10 text-center"
            max={5120}
            min={-5120}
            onChange={(val) => handleInputChange('left', val)}
            onChangeComplete={handleInputChangeComplete('left')}
            step={1}
            value={Number((values.left || '0px')?.replace('px', ''))}
            disabled={!positionAnchors.enableLeft}
          />
          <PositionControlLine
            value={positionAnchors.enableLeft}
            state={'horizontal'}
            disabled={false}
            onClick={() => handlePositionControlLineClick('enableLeft')}
          />
        </div>
        <PositionControlSquare
          onClick={() => PositionControlSquareClick()}
          value={positionAnchors.enabled}
          disabled={false}
        />
        <div className="flex flex-row items-center gap-1">
          <PositionControlLine
            value={positionAnchors.enableRight}
            state={'horizontal'}
            disabled={false}
            onClick={() => handlePositionControlLineClick('enableRight')}
          />

          <NumberField
            className="h-8 min-h-8 w-12 min-w-10 text-center"
            max={5120}
            min={-5120}
            onChange={(val) => handleInputChange('right', val)}
            onChangeComplete={handleInputChangeComplete('right')}
            step={1}
            value={Number((values.right || '0px')?.replace('px', ''))}
            disabled={!positionAnchors.enableRight}
          />
        </div>
      </div>
      <div className="flex flex-col items-center gap-1">
        <PositionControlLine
          value={positionAnchors.enableBottom}
          state={'vertical'}
          disabled={false}
          onClick={() => handlePositionControlLineClick('enableBottom')}
        />

        <NumberField
          className="h-8 min-h-8 w-12 min-w-10 text-center"
          max={5120}
          min={-5120}
          onChange={(val) => handleInputChange('bottom', val)}
          onChangeComplete={handleInputChangeComplete('bottom')}
          step={1}
          value={Number((values.bottom || '0px')?.replace('px', ''))}
          disabled={!positionAnchors.enableBottom}
        />
      </div>
    </div>
  );
};
